'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mantine/core';
import { Carousel, Embla } from '@mantine/carousel';
import { ChevronLeft, ChevronRight, Target } from 'lucide-react';
import { RootBox } from '@/shared/RootBox';
import { SectionTitle } from '@/shared/SectionTitle';
import { SectionSubtitle } from '@/shared/SectionSubtitle';

type Slide = {
  label: string;
  title: string;
  subtitle: string;
  bullets: string[];
};

const slidesContent: Slide[] = [
  {
    label: 'I',
    title: 'DISCOVERY & PRE-SELECTION',
    subtitle: 'Build the complete global investable universe',
    bullets: [
      'Proprietary database of 38,000+ growth and late-stage companies.',
      'Filters applied by Region, Valuation, Stage, Revenue Growth, ARR, CAGR, Investor Efficiency Index, etc.',
      'Continuous data monitoring to capture emerging opportunities beyond traditional networks.',
    ],
  },
  {
    label: 'II',
    title: 'RESEARCH & SELECTION',
    subtitle: 'Identify and evaluate top opportunities objectively',
    bullets: [
      'Rich 10Year Machine Learning metrics + proprietary algorithms ensure consistent, AI and human bias-free evaluation over time.',
      'Preliminary scoring based on revenue, valuation growth, and capital efficiency.',
      'Top-ranked companies undergo Deep Research, integrating additional metrics like projected fair value, IRR, and risk level.',
      'Auditable trace logs for full transparency',
    ],
  },
  {
    label: 'III',
    title: 'SYSTEMATIC RISK MANAGEMENT',
    subtitle: 'Simulate and optimize investment performance',
    bullets: [
      'Define entry/exit strategies and allocation rules.',
      'Run systematic simulations across multiple periods to project IRR, Cash-on-Cash, and Sharpe ratios.',
      'Real-time benchmarking and rebalancing tools for dynamic portfolio optimization.',
      'Systematic company selection based on portfolio-wide risk comparison to minimize overall portfolio exposure.',
    ],
  },
];

const BulletIcon = () => (
  <span
    aria-hidden="true"
    className="mt-1 inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-primary-500/70"
  >
    <span className="size-1.5 rounded-full bg-primary-500" />
  </span>
);

const StrategyCard = ({ slide }: { slide: Slide }) => {
  return (
    <div className="w-full rounded-3xl border min-h-[380px] border-black/5 bg-lightBlue-100/70 p-8 dark:border-white/10 dark:bg-secondary-500/15">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-montserrat text-lg font-bold uppercase tracking-wider text-secondary-600 dark:text-lightBlue-200">
          {slide.title}
        </h3>
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-primary-500/40 bg-white/70 text-base font-semibold text-primary-500 shadow-sm dark:bg-secondary-500/20">
          {slide.label}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-4">
        <div className="mt-0.5 inline-flex size-10 items-center justify-center rounded-full border border-black/5 bg-white/60 text-secondary-500 dark:border-white/10 dark:bg-white/10 dark:text-lightBlue-200">
          <Target className="size-6" />
        </div>
        <p className="text-base font-medium text-secondary-500 dark:text-lightBlue-200">
          {slide.subtitle}
        </p>
      </div>

      <div className="mt-6 h-px w-full bg-secondary-500/15 dark:bg-white/10" />

      <ul className="mt-6 space-y-4 text-sm leading-relaxed text-secondary-500 dark:text-lightBlue-200/90">
        {slide.bullets.map((bullet) => (
          <li key={bullet} className="flex gap-3">
            <BulletIcon />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

type CarouselControlsProps = {
  embla: Embla | null;
  onInteract: () => void;
};

const CarouselControls = ({ embla, onInteract }: CarouselControlsProps) => {
  return (
    <div className="mt-6 flex items-center justify-center gap-4">
      <button
        type="button"
        aria-label="Previous slide"
        className="inline-flex size-10 items-center justify-center rounded-full bg-primary-500 text-white shadow-sm transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 focus:ring-offset-transparent"
        onClick={() => {
          embla?.scrollPrev();
          onInteract();
        }}
      >
        <ChevronLeft className="size-5" />
      </button>
      <button
        type="button"
        aria-label="Next slide"
        className="inline-flex size-10 items-center justify-center rounded-full bg-primary-500 text-white shadow-sm transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 focus:ring-offset-transparent"
        onClick={() => {
          embla?.scrollNext();
          onInteract();
        }}
      >
        <ChevronRight className="size-5" />
      </button>
    </div>
  );
};

export const SystematicStrategies = () => {
  const [embla, setEmbla] = useState<Embla | null>(null);
  const autoplayIntervalRef = useRef<number | null>(null);
  const autoplayPausedRef = useRef(false);
  const autoplayDelayMs = 4500;

  const clearAutoplay = () => {
    if (autoplayIntervalRef.current === null) return;
    window.clearInterval(autoplayIntervalRef.current);
    autoplayIntervalRef.current = null;
  };

  const startAutoplay = () => {
    if (!embla) return;
    clearAutoplay();
    autoplayIntervalRef.current = window.setInterval(() => {
      if (autoplayPausedRef.current) return;
      embla.scrollNext();
    }, autoplayDelayMs);
  };

  const pauseAutoplay = () => {
    autoplayPausedRef.current = true;
  };

  const resumeAutoplay = () => {
    autoplayPausedRef.current = false;
  };

  useEffect(() => {
    if (!embla) return;
    startAutoplay();
    return () => clearAutoplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embla]);

  return (
    <Box
      className="relative z-0 w-full"
      id="systematicstrategiesforventureinvesting"
      style={{ overflow: 'hidden' }}
    >
      <RootBox>
        <SectionTitle title="OUR INVESTMENT APPROACH" index={3} />
        <SectionSubtitle subtitle="Systematic Strategies for Venture Investing" />

        <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16 pb-2">
          <div className="space-y-4 max-w-[680px] text-xl leading-relaxed text-secondary-500 dark:text-lightBlue-200/90 xxs:max-md:text-base">
            <p>
              <span className="font-semibold text-primary-500">Arcanis Capital</span> combines the rigor of quantitative investing with the opportunity of private markets.
            </p>
            <p>
              Our platform continuously analyzes over 38,000 growth and late-stage companies, powered by AI agents that work alongside human expertise to provide actionable insights.
            </p>
            <p>
              This collaboration enables a repeatable investment process that replaces intuition with evidence.
            </p>
            <p>
              We identify a range of companies with the highest growth potential based on agreed, predefined parameters.
            </p>

          </div>

          <div className="w-full">
            <Carousel
              getEmblaApi={setEmbla}
              withControls={false}
              withIndicators={false}
              loop
              slideGap="md"
              onMouseEnter={pauseAutoplay}
              onMouseLeave={() => {
                resumeAutoplay();
                startAutoplay();
              }}
              onFocusCapture={pauseAutoplay}
              onBlurCapture={() => {
                resumeAutoplay();
                startAutoplay();
              }}
              styles={{
                viewport: { borderRadius: 24 },
              }}
            >
              {slidesContent.map((slide, index) => (
                <Carousel.Slide key={index}>
                  <div className="animate-fadeIn">
                    <StrategyCard slide={slide} />
                  </div>
                </Carousel.Slide>
              ))}
            </Carousel>

            <CarouselControls embla={embla} onInteract={startAutoplay} />
          </div>
        </div>
      </RootBox>
    </Box>
  );
};

SystematicStrategies.displayName = 'SystematicStrategies';
